import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { BulletList, BulletListItem, Button, Callout, Column, CustomModal, Row, Spinner, Tooltip, Typography, Spacing } from 'seagull';
import { useOverlayContext } from 'seagull/components/OverlayContext/OverlayContext';
import { Send } from 'seagull/icons/emails-messages';
import { Checkmark, Info } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { useBulkSendProposals } from '~/pages/DraftsDashboard/hooks/useBulkSendProposals';
import { CountEmphasis } from '../CountEmphasis/CountEmphasis';
import { useCancelTask } from '../hooks/useCancelTask';
import { bulkCreateRunTitle } from '../runResults.utils';
import styles from './BulkCreateResultsModal.module.scss';
import { failedHeadline, failedRowDetail, identifiableFailures, sendAllLabel, summaryLine, unreachedClientsLine } from './BulkCreateResultsModal.utils';
import { CreatedDraftsTable } from './CreatedDraftsTable';
import { useBulkCreateResults } from './hooks/useBulkCreateResults';
export const GetBulkCreateResultsModalTID = (parentTestId)=>getTestIds(parentTestId);
const SEND_ALL_OPTIONS = {
    confirmFirst: false,
    feedbackFeature: 'bulkCreateProposals'
};
export const BulkCreateResultsModal = (param)=>{
    let { runId, jobName, runCancelled, onClose, onReviewDrafts, testId } = param;
    const { t: drawerTexts } = useLocaleKeys('navigation.tasksDrawer');
    const { t: errorTexts } = useLocaleKeys('errors');
    const { showErrorToast } = useOverlayContext();
    const texts = drawerTexts.bulkCreateDrafts;
    const { BulkCreateResultsModalTestIds } = useMemo(()=>GetBulkCreateResultsModalTID(testId), [
        testId
    ]);
    const { results, createdDrafts, isLoading, failed } = useBulkCreateResults(runId);
    const { bulkSendProposals } = useBulkSendProposals();
    const { cancelTask } = useCancelTask();
    const reportedFailure = useRef(false);
    useEffect(()=>{
        if (!failed || reportedFailure.current) return;
        reportedFailure.current = true;
        showErrorToast(errorTexts.unexpected(), BulkCreateResultsModalTestIds.LoadFailed());
        onClose();
    }, [
        failed,
        onClose,
        BulkCreateResultsModalTestIds
    ]);
    const [sending, setSending] = useState(false);
    const hasDraftsToSend = createdDrafts.length > 0;
    const createdDraftIds = createdDrafts.map((draft)=>draft.id);
    const sendAll = async ()=>{
        setSending(true);
        const batchId = await bulkSendProposals(createdDraftIds, _object_spread_props(_object_spread({}, SEND_ALL_OPTIONS), {
            jobName
        }));
        setSending(false);
        if (batchId == null) return;
        cancelTask(runId);
        onClose();
    };
    return /*#__PURE__*/ _jsx(CustomModal, {
        testId: BulkCreateResultsModalTestIds.Root(),
        width: "min(860px, 94vw)",
        maxWidth: 860,
        disableFooterPadding: true,
        onClose: onClose,
        bi: {
            closeButton: {
                name: 'bulk_create_results_close'
            }
        },
        header: /*#__PURE__*/ _jsx(Row, {
            justifyContent: "center",
            children: /*#__PURE__*/ _jsxs(Row, {
                gap: Spacing.Space8,
                alignItems: "center",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "h6",
                        weight: "normal",
                        testId: BulkCreateResultsModalTestIds.Title(),
                        children: bulkCreateRunTitle(jobName, texts)
                    }),
                    /*#__PURE__*/ _jsx(Tooltip, {
                        title: texts.frozenTemplate(),
                        children: /*#__PURE__*/ _jsx(Column, {
                            className: styles.frozenTemplate,
                            testId: BulkCreateResultsModalTestIds.FrozenTemplate(),
                            children: /*#__PURE__*/ _jsx(Info, {
                                size: 16
                            })
                        })
                    })
                ]
            })
        }),
        footer: results && /*#__PURE__*/ _jsxs(Row, {
            justifyContent: "center",
            gap: Spacing.Space12,
            className: styles.footer,
            children: [
                /*#__PURE__*/ _jsx(Button, {
                    skin: "primaryOutline",
                    bi: {
                        name: 'bulk_create_results_review_drafts'
                    },
                    testId: BulkCreateResultsModalTestIds.ReviewDraftsButton(),
                    onClick: onReviewDrafts,
                    children: texts.modalReviewDrafts()
                }),
                /*#__PURE__*/ _jsx(Button, {
                    prefixIcon: /*#__PURE__*/ _jsx(Send, {}),
                    loading: sending,
                    disabled: !hasDraftsToSend,
                    tooltip: hasDraftsToSend ? undefined : {
                        title: texts.modalSendAllNoneLeft()
                    },
                    bi: {
                        name: 'bulk_create_results_send_all'
                    },
                    testId: BulkCreateResultsModalTestIds.SendAllButton(),
                    onClick: sendAll,
                    children: sendAllLabel(createdDrafts.length, texts)
                })
            ]
        }),
        children: /*#__PURE__*/ _jsxs(Column, {
            gap: Spacing.Space16,
            className: styles.content,
            children: [
                isLoading && /*#__PURE__*/ _jsx(Spinner, {
                    centeredInBox: true,
                    centerBoxHeight: "auto",
                    testId: BulkCreateResultsModalTestIds.Loader()
                }),
                !isLoading && !failed && results && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            gap: 10,
                            alignItems: "start",
                            children: [
                                runCancelled ? /*#__PURE__*/ _jsx(Info, {
                                    size: 20,
                                    className: styles.cancelledIcon
                                }) : /*#__PURE__*/ _jsx(Checkmark, {
                                    size: 20,
                                    className: styles.checkIcon
                                }),
                                /*#__PURE__*/ _jsxs(Column, {
                                    gap: Spacing.Space2,
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            weight: runCancelled ? 'normal' : undefined,
                                            testId: BulkCreateResultsModalTestIds.Summary(),
                                            children: runCancelled ? summaryLine(createdDrafts.length, runCancelled, texts) : /*#__PURE__*/ _jsx(CountEmphasis, {
                                                text: summaryLine(createdDrafts.length, runCancelled, texts),
                                                count: createdDrafts.length,
                                                testId: BulkCreateResultsModalTestIds.Summary()
                                            })
                                        }),
                                        runCancelled && results.cancelled.length > 0 && /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph2",
                                            skin: "secondary",
                                            testId: BulkCreateResultsModalTestIds.CancelledDetail(),
                                            children: unreachedClientsLine(results, texts)
                                        })
                                    ]
                                })
                            ]
                        }),
                        results.failed.length > 0 && /*#__PURE__*/ _jsx(Callout, {
                            intent: "error",
                            iconSize: "small",
                            iconAlignment: "firstLine",
                            testId: BulkCreateResultsModalTestIds.Failures(),
                            children: /*#__PURE__*/ _jsxs(Column, {
                                gap: Spacing.Space4,
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        children: /*#__PURE__*/ _jsx(CountEmphasis, {
                                            text: failedHeadline(results.failed.length, texts),
                                            count: results.failed.length,
                                            testId: BulkCreateResultsModalTestIds.Failures()
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(BulletList, {
                                        spacing: "compact",
                                        children: identifiableFailures(results).map((client)=>/*#__PURE__*/ _jsxs(BulletListItem, {
                                                variant: "paragraph3",
                                                bulletSize: "small",
                                                testId: BulkCreateResultsModalTestIds.FailedRow(client.clientId),
                                                children: [
                                                    /*#__PURE__*/ _jsx(Typography, {
                                                        as: "span",
                                                        variant: "paragraph3",
                                                        weight: "bold",
                                                        children: client.clientName
                                                    }),
                                                    ' ',
                                                    failedRowDetail(client, texts)
                                                ]
                                            }, client.clientId))
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Column, {
                            className: styles.table,
                            children: /*#__PURE__*/ _jsx(CreatedDraftsTable, {
                                drafts: createdDrafts,
                                testId: BulkCreateResultsModalTestIds.Table()
                            })
                        })
                    ]
                })
            ]
        })
    });
};
